import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { API_BASE, get } from "../../api/client";
import type { MyMailbox } from "../../api/types";
import { appInBackground, showNotification } from "../../lib/notifications";

export const emailUnreadKey = ["email", "unread-count"] as const;

/** Unread emails in your inboxes, for the menu badge (refreshed instantly by live events). */
export function useEmailUnread(enabled: boolean) {
  return useQuery({
    queryKey: emailUnreadKey,
    queryFn: () => get<{ unread: number }>("/email/unread-count"),
    enabled,
    refetchInterval: () => (document.visibilityState === "visible" ? 30_000 : 120_000),
    refetchIntervalInBackground: true,
  });
}

/**
 * Keeps one live connection (Server-Sent Events) open while signed in with email access. New mail
 * refreshes the email views and badge at once, and shows a desktop notification if the app is in the
 * background. The browser reconnects by itself; polling covers any gap.
 */
export function useEmailLive(enabled: boolean, onOpen: (mailboxId: string, emailId: string) => void) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!enabled || typeof EventSource === "undefined") return;
    const source = new EventSource(`${API_BASE}/events`, { withCredentials: true });
    const refresh = () => void qc.invalidateQueries({ queryKey: ["email"] });
    const onReceived = (ev: MessageEvent<string>) => {
      refresh();
      try {
        const { mailboxId, emailId } = JSON.parse(ev.data) as { mailboxId: string; emailId: string };
        if (appInBackground()) {
          const box = qc.getQueryData<{ items: MyMailbox[] }>(["email", "mailboxes"])?.items.find((m) => m.id === mailboxId);
          showNotification("New email", { body: box ? `In ${box.address}` : "You have new mail", tag: `email-${emailId}`, onClick: () => onOpen(mailboxId, emailId) });
        }
      } catch { /* ignore malformed events */ }
    };
    source.addEventListener("email.received", onReceived as EventListener);
    source.addEventListener("email.sent", refresh);
    source.addEventListener("email.updated", refresh);
    for (const type of ["email.delivered", "email.bounced", "email.read"]) source.addEventListener(type, refresh);
    return () => source.close();
  }, [enabled, qc, onOpen]);
}
